import { useEffect, useState } from 'react';
import { useLazyGetLoadRequestsQuery } from '@epi-api/loadRequests/loadRequestsApi';
import {
  BalanceCorrectionDate,
  LoadRequestDetails
} from '@epi-models/pages/Reports';
import { getEndOfDay, getStartOfDay } from '@epi-helpers/dateHelper';

const TOOLTIP_LABELS = {
  request_order_status_combination_4_3: 'pending_tooltip',
  request_order_status_combination_4_1: 'pending_tooltip',
  request_order_status_combination_10_8: 'failed_tooltip',
  default: ''
};

const TRANSACTION_STATUS_LABELS = {
  request_order_status_combination_0_1: 'top_up_load_status.in_progress',
  request_order_status_combination_3_3: 'top_up_load_status.in_progress',
  request_order_status_combination_4_3: 'top_up_load_status.pending',
  request_order_status_combination_4_1: 'top_up_load_status.pending',
  request_order_status_combination_5_6: 'top_up_load_status.completed',
  request_order_status_combination_7_7: 'top_up_load_status.canceled',
  request_order_status_combination_8_7: 'top_up_load_status.canceled',
  request_order_status_combination_8_8: 'top_up_load_status.canceled',
  request_order_status_combination_10_8: 'top_up_load_status.failed',
  request_order_status_combination_12_11: 'top_up_load_status.in_progress',
  default: ''
};

export const useLoadRequests = () => {
  const [reports, setReports] = useState<LoadRequestDetails[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);
  const [timePeriodType, setTimePeriodType] = useState<string>();
  const [from, setFrom] = useState<BalanceCorrectionDate>(null);
  const [to, setTo] = useState<BalanceCorrectionDate>(null);

  const [trigger, { isLoading, isError }] = useLazyGetLoadRequestsQuery();

  const createTooltipLabelKey = (loadRequestDetail: LoadRequestDetails) => {
    const key = `${loadRequestDetail.requestStatusId}_${loadRequestDetail.orderStatusId}`;
    return (
      TOOLTIP_LABELS[`request_order_status_combination_${key}`] ||
      TOOLTIP_LABELS['default']
    );
  };

  const createTransactionStatusLabelKey = (
    loadRequestDetail: LoadRequestDetails
  ) => {
    const key = `${loadRequestDetail.requestStatusId}_${loadRequestDetail.orderStatusId}`;
    return (
      TRANSACTION_STATUS_LABELS[`request_order_status_combination_${key}`] ||
      TRANSACTION_STATUS_LABELS['default']
    );
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const getReports = async () => {
    try {
      const formattedFrom = from
        ? getStartOfDay(new Date(from)).toISOString()
        : '';
      const formattedTo = to ? getEndOfDay(new Date(to)).toISOString() : '';

      const response = await trigger({
        page,
        from: formattedFrom,
        to: formattedTo,
        abortController: null
      });

      if (response.data) {
        const reports = response.data.items.map(r => ({
          ...r,
          tooltip: createTooltipLabelKey(r),
          transactionStatus: createTransactionStatusLabelKey(r)
        }));

        setReports(reports);
        setTotalPages(response.data.totalPages);
      }
    } catch (error) {
      console.error('Error fetching load requests:', error);
    }
  };

  useEffect(() => {
    getReports();
  }, [page, from, to]);

  return {
    reports,
    page,
    totalPages,
    isLoading,
    isError,
    showCalendar,
    setShowCalendar,
    timePeriodType,
    handleChangePage,
    from,
    to,
    setFrom,
    setTo,
    setPage,
    setTimePeriodType
  };
};
